import { useEffect, useState, useMemo } from "react";
import { PageHeader, PageBody } from "@/components/AppLayout";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import {
  History, Filter, X, ArrowRight, Pencil, Plus, Trash2,
  Layers, User as UserIcon, Search, RotateCcw,
} from "lucide-react";

const ENTITY_LABELS = {
  transaction: "Transaction",
  invoice: "Invoice",
  bank_account: "Bank account",
  custom_category: "Custom category",
  client_workspace: "Client workspace",
  recon_signoff: "Reconciliation sign-off",
  statement: "Statement",
};

const ACTION_META = {
  create:      { label: "Created",       Icon: Plus,    tone: "#065F46", bg: "#ECFDF5" },
  update:      { label: "Edited",        Icon: Pencil,  tone: "#1E40AF", bg: "#EFF6FF" },
  delete:      { label: "Deleted",       Icon: Trash2,  tone: "#991B1B", bg: "#FEF2F2" },
  bulk_update: { label: "Bulk edited",   Icon: Layers,  tone: "#5B21B6", bg: "#F5F3FF" },
  bulk_delete: { label: "Bulk deleted",  Icon: Layers,  tone: "#9A3412", bg: "#FFF7ED" },
};

const PAGE_SIZE = 50;

const formatValue = (v) => {
  if (v === null || v === undefined || v === "") return <span className="opacity-50 italic">empty</span>;
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "number") {
    // Money-ish — show 2 decimals if the field looks like a currency amount
    return Number.isInteger(v) ? v.toLocaleString() : v.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }
  if (Array.isArray(v)) return `[${v.length} items]`;
  if (typeof v === "object") return JSON.stringify(v).slice(0, 80);
  const s = String(v);
  return s.length > 80 ? `${s.slice(0, 77)}…` : s;
};

const DiffBadge = ({ field, before, after }) => (
  <div className="flex items-start gap-2 text-xs">
    <div className="text-[10px] uppercase tracking-wider text-[var(--ll-ink-muted)] shrink-0 pt-0.5 w-28 truncate" title={field}>{field}</div>
    <div className="flex flex-wrap items-center gap-1.5 min-w-0 flex-1">
      <span className="inline-block px-2 py-0.5 bg-[#FEF2F2] text-[#991B1B] border border-[#FCA5A5] max-w-[220px] truncate">{formatValue(before)}</span>
      <ArrowRight size={11} className="opacity-60 shrink-0" />
      <span className="inline-block px-2 py-0.5 bg-[#ECFDF5] text-[#065F46] border border-[#86EFAC] max-w-[220px] truncate">{formatValue(after)}</span>
    </div>
  </div>
);

export default function AuditLog() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actors, setActors] = useState([]);
  const [filters, setFilters] = useState({
    entity_type: "",
    action: "",
    user_id: "",
    start: "",
    end: "",
    search: "",
  });
  const [expanded, setExpanded] = useState({});  // id -> bool

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters],
  );

  const loadActors = async () => {
    try {
      const { data } = await api.get("/audit-logs/actors");
      setActors(data || []);
    } catch (err) {
      // Non-fatal — just means the dropdown stays empty
    }
  };

  const load = async (nextSkip = 0, mergedFilters = filters) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: nextSkip };
      Object.entries(mergedFilters).forEach(([k, v]) => {
        if (v) params[k] = v;
      });
      const { data } = await api.get("/audit-logs", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSkip(nextSkip);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
    loadActors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = () => load(0);
  const resetFilters = () => {
    const empty = { entity_type: "", action: "", user_id: "", start: "", end: "", search: "" };
    setFilters(empty);
    load(0, empty);
  };

  const toggle = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const nextPage = () => load(skip + PAGE_SIZE);
  const prevPage = () => load(Math.max(0, skip - PAGE_SIZE));

  return (
    <>
      <PageHeader
        title="Audit log"
        subtitle="Every manual change to transactions, invoices, bank accounts and categories — who, what, when."
        icon={<History size={22} />}
      />
      <PageBody>
        {/* Filter bar */}
        <div className="bg-white border p-3 sm:p-4 mb-4" style={{ borderColor: "var(--ll-border)" }}>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--ll-ink-muted)] mb-3">
            <Filter size={13} /> Filters
            {activeFilterCount > 0 && (
              <span className="bg-[var(--ll-mint)] text-[var(--ll-ink)] px-1.5 py-0.5 normal-case tracking-normal font-medium">
                {activeFilterCount} active
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            <div className="lg:col-span-2">
              <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Search</label>
              <div className="relative">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 opacity-50" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                  placeholder="Entity label or user email…"
                  data-testid="audit-filter-search"
                  className="w-full pl-7 pr-3 py-2 border text-sm bg-white"
                  style={{ borderColor: "var(--ll-border)" }}
                />
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Entity</label>
              <select
                value={filters.entity_type}
                onChange={(e) => setFilters({ ...filters, entity_type: e.target.value })}
                data-testid="audit-filter-entity"
                className="w-full px-2 py-2 border text-sm bg-white"
                style={{ borderColor: "var(--ll-border)" }}
              >
                <option value="">All</option>
                {Object.entries(ENTITY_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Action</label>
              <select
                value={filters.action}
                onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                data-testid="audit-filter-action"
                className="w-full px-2 py-2 border text-sm bg-white"
                style={{ borderColor: "var(--ll-border)" }}
              >
                <option value="">All</option>
                {Object.entries(ACTION_META).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">User</label>
              <select
                value={filters.user_id}
                onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                data-testid="audit-filter-user"
                className="w-full px-2 py-2 border text-sm bg-white"
                style={{ borderColor: "var(--ll-border)" }}
              >
                <option value="">Everyone</option>
                {actors.map((a) => (
                  <option key={a.user_id} value={a.user_id}>
                    {a.name || a.email || a.user_id?.slice(0, 8)}{a.count ? ` (${a.count})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={applyFilters}
                data-testid="audit-apply-filters-btn"
                className="flex-1 bg-[var(--ll-ink)] text-white py-2 text-sm hover:bg-[#0b5d9e] transition-colors"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={resetFilters}
                data-testid="audit-reset-filters-btn"
                aria-label="Reset filters"
                className="px-2 py-2 border text-sm hover:bg-[var(--ll-mint)] transition-colors"
                style={{ borderColor: "var(--ll-border)" }}
              >
                <RotateCcw size={13} />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">From</label>
              <input
                type="date"
                value={filters.start?.slice(0, 10) || ""}
                onChange={(e) => setFilters({ ...filters, start: e.target.value ? `${e.target.value}T00:00:00Z` : "" })}
                data-testid="audit-filter-start"
                className="w-full px-2 py-2 border text-sm bg-white"
                style={{ borderColor: "var(--ll-border)" }}
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">To</label>
              <input
                type="date"
                value={filters.end?.slice(0, 10) || ""}
                onChange={(e) => setFilters({ ...filters, end: e.target.value ? `${e.target.value}T23:59:59Z` : "" })}
                data-testid="audit-filter-end"
                className="w-full px-2 py-2 border text-sm bg-white"
                style={{ borderColor: "var(--ll-border)" }}
              />
            </div>
          </div>
        </div>

        {/* Entries list */}
        <div className="bg-white border" style={{ borderColor: "var(--ll-border)" }} data-testid="audit-list">
          <div className="px-4 py-2.5 border-b text-xs uppercase tracking-widest text-[var(--ll-ink-muted)] flex items-center justify-between" style={{ borderColor: "var(--ll-border)" }}>
            <span>{loading ? "Loading…" : `${total.toLocaleString()} entries`}</span>
            {total > PAGE_SIZE && (
              <div className="flex items-center gap-2 normal-case tracking-normal text-[var(--ll-ink)]">
                <button
                  type="button"
                  onClick={prevPage}
                  disabled={skip === 0 || loading}
                  data-testid="audit-prev-btn"
                  className="px-2 py-1 border text-xs disabled:opacity-40"
                  style={{ borderColor: "var(--ll-border)" }}
                >Prev</button>
                <span className="text-xs">{skip + 1}–{Math.min(skip + PAGE_SIZE, total)}</span>
                <button
                  type="button"
                  onClick={nextPage}
                  disabled={skip + PAGE_SIZE >= total || loading}
                  data-testid="audit-next-btn"
                  className="px-2 py-1 border text-xs disabled:opacity-40"
                  style={{ borderColor: "var(--ll-border)" }}
                >Next</button>
              </div>
            )}
          </div>
          {!loading && items.length === 0 && (
            <div className="p-10 text-center text-sm text-[var(--ll-ink-muted)]" data-testid="audit-empty">
              <History size={28} className="mx-auto mb-3 opacity-40" />
              No audit entries match your filters yet.
            </div>
          )}
          <ul className="divide-y" style={{ borderColor: "var(--ll-border)" }}>
            {items.map((it) => {
              const meta = ACTION_META[it.action] || ACTION_META.update;
              const Icon = meta.Icon;
              const isOpen = !!expanded[it.id];
              return (
                <li key={it.id} data-testid={`audit-row-${it.id}`} className="px-3 sm:px-4 py-3">
                  <div className="flex items-start gap-3">
                    <div
                      className="w-7 h-7 shrink-0 grid place-items-center mt-0.5"
                      style={{ background: meta.bg, color: meta.tone }}
                    >
                      <Icon size={13} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        <span className="text-sm font-medium" style={{ color: meta.tone }}>{meta.label}</span>
                        <span className="text-sm">{ENTITY_LABELS[it.entity_type] || it.entity_type}</span>
                        <span className="text-xs text-[var(--ll-ink-muted)] truncate" title={it.entity_label}>
                          {it.entity_label || it.entity_id?.slice(0, 8)}
                        </span>
                      </div>
                      <div className="text-xs text-[var(--ll-ink-muted)] mt-0.5 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1">
                          <UserIcon size={11} />{it.user_name || it.user_email || "system"}
                        </span>
                        <span>·</span>
                        <span title={it.timestamp}>{fmtDate(it.timestamp)} · {it.timestamp?.slice(11, 16)}</span>
                        {(it.diff?.length || 0) > 0 && (
                          <>
                            <span>·</span>
                            <button
                              type="button"
                              onClick={() => toggle(it.id)}
                              data-testid={`audit-toggle-${it.id}`}
                              className="underline underline-offset-2 hover:text-[var(--ll-ink)]"
                            >
                              {isOpen ? "Hide" : "Show"} {it.diff.length} change{it.diff.length === 1 ? "" : "s"}
                            </button>
                          </>
                        )}
                      </div>
                      {isOpen && (it.diff?.length || 0) > 0 && (
                        <div className="mt-2 pl-0.5 space-y-1.5">
                          {it.diff.map((d, idx) => (
                            <DiffBadge key={idx} field={d.field} before={d.before} after={d.after} />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </PageBody>
    </>
  );
}
